import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Layout from './Layout';

interface InformationPage {
  _id: string;
  title: string;
  slug: string;
  content: string;
  description: string;
  category: string;
  isPublished: boolean;
  featured: boolean;
  tags: string[];
  imageUrl?: string;
  author: string;
  priority: number;
  status: string;
  createdAt?: string;
}

interface ServiceProject {
  _id: string;
  title: string;
  slug: string;
  description: string;
  imageUrl: string;
  category: string;
  network: string;
  tags: string[];
  featured: boolean;
}

const InformationPageDisplay: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<InformationPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [allProjects, setAllProjects] = useState<ServiceProject[]>([]);
  const [showServicesModal, setShowServicesModal] = useState(false);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        // Завантажуємо інформаційні сторінки з localStorage
        const savedData = localStorage.getItem('informationPages');
        if (savedData) {
          const pages: InformationPage[] = JSON.parse(savedData);
          const foundPage = pages.find(p => p.slug === slug && p.isPublished);
          
          if (foundPage) {
            console.log('✅ Found Information Page:', foundPage.title);
            setPage(foundPage);
            setLoading(false);
            return;
          }
        }

        // Завантажуємо всі сервіси для модалю
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        try {
          const response = await fetch(`${apiUrl}/api/projects?status=active&isPublished=true`);
          const data = await response.json();
          if (data.success) {
            setAllProjects(data.data.projects);
          }
        } catch (error) {
          console.error('Error fetching projects:', error);
        }
      } catch (error) {
        console.error('Error loading page:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <Layout currentPage="other" showServicesModal={showServicesModal} setShowServicesModal={setShowServicesModal} projects={allProjects} headerVariant="main">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 mb-4">
              <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-white text-lg">Loading page...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!page) {
    return (
      <Navigate to="/" replace />
    );
  }

  // Показуємо Information Page
  return (
    <Layout currentPage="other" showServicesModal={showServicesModal} setShowServicesModal={setShowServicesModal} projects={allProjects} headerVariant="main">
      <article className="max-w-4xl mx-auto px-6 py-12">
        {/* Title Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {page?.title}
          </h1>
          {page && page.description && (
            <p className="text-lg text-gray-300">
              {page.description}
            </p>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-700 my-8"></div>

        {/* Content */}
        {page && (
          <>
            <style>{`
              .info-page-content { white-space: normal; word-wrap: break-word; }
              .info-page-content h1 { font-size: 2rem; font-weight: bold; color: white; margin-bottom: 1.5rem; margin-top: 2rem; }
              .info-page-content h1:first-child { margin-top: 0; }
              .info-page-content h2 { font-size: 1.875rem; font-weight: bold; color: white; margin-bottom: 1rem; margin-top: 1.5rem; }
              .info-page-content h3 { font-size: 1.5rem; font-weight: bold; color: white; margin-bottom: 0.75rem; margin-top: 1rem; }
              .info-page-content h4 { font-size: 1.25rem; font-weight: bold; color: white; margin-bottom: 0.5rem; margin-top: 0.75rem; }
              .info-page-content p { margin-bottom: 1.25rem; color: #d1d5db; line-height: 1.8; }
              .info-page-content a { color: #60a5fa; text-decoration: none; }
              .info-page-content a:hover { color: #93c5fd; text-decoration: underline; }
              .info-page-content strong { color: white; font-weight: bold; }
              .info-page-content em { font-style: italic; color: #d1d5db; }
              .info-page-content code { background-color: #1f2937; color: #d1d5db; padding: 0.25rem 0.5rem; border-radius: 0.25rem; font-size: 0.875rem; font-family: monospace; }
              .info-page-content pre { background-color: #1f2937; color: #d1d5db; padding: 1rem; border-radius: 0.375rem; overflow-x: auto; margin-bottom: 1.5rem; margin-top: 1rem; white-space: pre-wrap; word-wrap: break-word; }
              .info-page-content pre code { background-color: transparent; padding: 0; color: inherit; }
              .info-page-content blockquote { border-left: 4px solid #60a5fa; padding-left: 1rem; color: #9ca3af; font-style: italic; margin-bottom: 1.5rem; margin-top: 1rem; margin-right: 0; }
              .info-page-content ul { list-style-type: disc; padding-left: 2rem; margin-bottom: 1.25rem; margin-top: 0.5rem; }
              .info-page-content ol { list-style-type: decimal; padding-left: 2rem; margin-bottom: 1.25rem; margin-top: 0.5rem; }
              .info-page-content li { margin-bottom: 0.75rem; color: #d1d5db; }
              .info-page-content table { width: 100%; border-collapse: collapse; margin-bottom: 1.5rem; margin-top: 1rem; }
              .info-page-content th { background-color: #374151; color: white; padding: 0.75rem; text-align: left; font-weight: bold; border: 1px solid #4b5563; }
              .info-page-content td { border: 1px solid #4b5563; padding: 0.75rem; color: #d1d5db; }
              .info-page-content hr { border: none; border-top: 1px solid #4b5563; margin: 2rem 0; }
            `}</style>
            <div
              className="info-page-content"
              dangerouslySetInnerHTML={{ __html: page.content }}
            />
          </>
        )}
      </article>
    </Layout>
  );
};

export default InformationPageDisplay;
